import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MessageTranslateService } from '../../../shared/services/message.service';
import { ChargerGroupsService } from '../services/charger-groups.service';
import * as chargerGroupActions from './charger-group.actions';
import * as operatorsActions from '../../../store/operators/operators.actions';
import { concatLatestFrom } from '@ngrx/operators';
import {
  selectCurrentGroupsFilter,
  selectCurrentPagination,
} from './charger-group.selectors';
import { ChargerGroupsRequestUtil } from '../utils/charger-groups-request.utils';
import { catchError, map, of, switchMap } from 'rxjs';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';

export class ChargerGroupsEffects {
  actions$: Actions = inject(Actions);
  chargerGroupsService: ChargerGroupsService = inject(ChargerGroupsService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);

  changedPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerGroupActions.changePagination),
      map((_) => chargerGroupActions.changePaginationSuccessful())
    )
  );

  loadChargerGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        chargerGroupActions.loadChargerGroupList,
        operatorsActions.SetCurrentOperatorSuccess,
        chargerGroupActions.updateCurrentGroupFilter,
        chargerGroupActions.changePaginationSuccessful,
        chargerGroupActions.saveChargerGroupSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(selectCurrentGroupsFilter),
        this.store.select(selectCurrentPagination),
      ]),
      switchMap(
        ([_action, selectedOperator, currentGroupsFilter, pagination]) => {
          return this.chargerGroupsService
            .searchChargerGroups(
              ChargerGroupsRequestUtil.createChargerGroupListRequest(
                selectedOperator!.id!,
                pagination,
                currentGroupsFilter
              )
            )
            .pipe(
              map((res) => {
                if (res.isSuccess) {
                  return chargerGroupActions.loadChargerGroupListSuccess({
                    chargerGroupsResponse: res.value!,
                  });
                } else {
                  return chargerGroupActions.loadChargerGroupListFailure({
                    error: res.error!,
                  });
                }
              }),
              catchError((err) =>
                of(
                  chargerGroupActions.loadChargerGroupListFailure({
                    error: err.message || 'Loading chargers failed',
                  })
                )
              )
            );
        }
      )
    )
  );

  loadGroupFilterValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerGroupActions.loadFilterOptions),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
      ]),
      switchMap(([_action, selectedOperator]) => {
        return this.chargerGroupsService
          .getFilterValues(selectedOperator!.id!)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return chargerGroupActions.loadFilterOptionsSuccess({
                  filterValues: {
                    maxNumOfChargers: res.value?.maxNumOfChargers,
                    maxNumOfConnectors: res.value?.maxNumOfConnectors,
                    maxEnergyInLast30Days: res.value?.maxEnergyInLast30Days,
                    maxNumberOfTransactionsInLast30Days:
                      res.value?.maxNumberOfTransactionsInLast30Days,
                  },
                });
              } else {
                return chargerGroupActions.loadFilterOptionsSuccess({
                  filterValues: null,
                });
              }
            }),
            catchError((err) =>
              of(
                chargerGroupActions.loadChargerGroupListFailure({
                  error: err.message || 'Loading chargers group failed',
                })
              )
            )
          );
      })
    )
  );
}
